import React, { useEffect, useMemo, useState } from 'react';
import { researchosService } from '../../services/researchosService';
import { SortableTh, useSortableRows } from '../../components/tables/SortableTable';

// Tracked X handles (superuser): what we hold per handle + the daily collector's since-marker
// (newest stored post). DB-derived, like the YouTube Coverage channel table. House rule: sortable by any column.
function fmtDay(s) {
  if (!s) return '—';
  try { return new Date(s).toISOString().slice(0, 10); } catch { return s; }
}
function fmtTime(s) {
  if (!s) return '—';
  try { return new Date(s).toLocaleString(); } catch { return s; }
}

const COLS = [
  { key: 'handle', type: 'string', get: (r) => r.handle },
  { key: 'display_name', type: 'string', get: (r) => r.display_name },
  { key: 'posts', type: 'number', get: (r) => r.posts },
  { key: 'days', type: 'number', get: (r) => r.days },
  { key: 'first_post_at', type: 'string', get: (r) => r.first_post_at || '' },
  { key: 'last_post_at', type: 'string', get: (r) => r.last_post_at || '' },
  { key: 'tweet_count_lifetime', type: 'number', get: (r) => r.tweet_count_lifetime || 0 },
  { key: 'last_checked_at', type: 'string', get: (r) => r.last_checked_at || '' },
];

export default function XHandlesPanel() {
  const [rows, setRows] = useState(null);
  const [err, setErr] = useState('');
  useEffect(() => {
    researchosService.getXHandles().then((r) => setRows(r.data || [])).catch(() => setErr('could not load handles'));
  }, []);
  const data = useMemo(() => rows || [], [rows]);
  const { sorted, toggle, indicator } = useSortableRows(data, COLS);

  if (err) return <p className="text-sm text-red-500">{err}</p>;
  if (rows == null) return <p className="text-sm text-gray-500 dark:text-gray-400">Loading…</p>;
  if (!rows.length) return <p className="text-sm text-gray-500 dark:text-gray-400">No handles yet — add one under Backfill &amp; Runs.</p>;

  const totPosts = rows.reduce((a, r) => a + (r.posts || 0), 0);
  return (
    <div>
      <div className="mb-3 text-xs text-gray-500 dark:text-gray-400">
        {rows.length} handle{rows.length === 1 ? '' : 's'} · {totPosts.toLocaleString()} posts stored · the daily collector reads each handle on x.com and pushes posts newer than its marker.
      </div>
      <div className="overflow-x-auto scrollbar-thin rounded-lg border border-gray-200 dark:border-gray-800">
        <table className="w-full text-sm">
          <thead className="text-[10px] uppercase tracking-[0.15em] text-gray-400">
            <tr>
              <SortableTh label="Handle" colKey="handle" toggle={toggle} indicator={indicator} align="left" className="px-3 py-2" />
              <SortableTh label="Name" colKey="display_name" toggle={toggle} indicator={indicator} align="left" className="px-3 py-2" />
              <SortableTh label="Posts" colKey="posts" toggle={toggle} indicator={indicator} className="px-3 py-2" />
              <SortableTh label="Days" colKey="days" toggle={toggle} indicator={indicator} className="px-3 py-2" />
              <SortableTh label="First" colKey="first_post_at" toggle={toggle} indicator={indicator} className="px-3 py-2" />
              <SortableTh label="Last" colKey="last_post_at" toggle={toggle} indicator={indicator} className="px-3 py-2" />
              <SortableTh label="Lifetime" colKey="tweet_count_lifetime" toggle={toggle} indicator={indicator} className="px-3 py-2" />
              <SortableTh label="Collector" colKey="last_checked_at" toggle={toggle} indicator={indicator} className="px-3 py-2" />
            </tr>
          </thead>
          <tbody>
            {sorted.map((r) => (
              <tr key={r.user_id} className="border-t border-gray-100 dark:border-gray-800/60">
                <td className="px-3 py-2 font-medium text-gray-900 dark:text-gray-100">
                  <a href={`https://x.com/${r.handle.replace('@', '')}`} target="_blank" rel="noreferrer" className="hover:underline">{r.handle}</a>
                  {r.official_of && <span className="ml-2 rounded bg-gray-100 px-1.5 py-0.5 text-[10px] text-gray-500 dark:bg-gray-800">official · {r.official_of}</span>}
                  {!r.enabled && <span className="ml-2 text-[10px] text-gray-400">disabled</span>}
                </td>
                <td className="px-3 py-2 text-gray-500 dark:text-gray-400">{r.display_name}</td>
                <td className="px-3 py-2 text-right tabular-nums text-gray-900 dark:text-gray-100">{(r.posts || 0).toLocaleString()}</td>
                <td className="px-3 py-2 text-right tabular-nums text-gray-600 dark:text-gray-300">{r.days}</td>
                <td className="px-3 py-2 text-right tabular-nums text-gray-500">{fmtDay(r.first_post_at)}</td>
                <td className="px-3 py-2 text-right tabular-nums text-gray-500">{fmtDay(r.last_post_at)}</td>
                <td className="px-3 py-2 text-right tabular-nums text-gray-400">{r.tweet_count_lifetime ? r.tweet_count_lifetime.toLocaleString() : '—'}</td>
                <td className="px-3 py-2 text-right text-xs text-gray-400" title={r.newest_post_id ? `newest stored post ${r.newest_post_id}` : ''}>{fmtTime(r.last_checked_at)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
